// MarketTab — Dashboard: indices, portfolio summary, gainers/losers (Angel One Light Theme)
import React, { useState, useEffect } from 'react';
import { useTrading } from '../../../context/TradingContext';
import { usePositionTracker } from '../../../hooks/usePositionTracker';
import { useIndexData } from '../../../hooks/useMarketData';
import PnLBadge from '../shared/PnLBadge';
import angelOneApi from '../../../services/angelOneApi';
import { FaArrowUp, FaArrowDown, FaWallet, FaBriefcase, FaChartLine, FaSync } from 'react-icons/fa';

export default function MarketTab() {
  const { connectionStatus, profile, refreshPortfolio } = useTrading();
  const { positionSummary, holdingSummary } = usePositionTracker();
  const indices = useIndexData();
  const [funds, setFunds] = useState(null);
  const [gainers, setGainers] = useState([]);
  const [losers, setLosers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Fetch funds/RMS
  useEffect(() => {
    if (connectionStatus !== 'connected') return;
    const fetchFunds = async () => {
      try {
        const result = await angelOneApi.getRMS();
        if (result.data?.data) {
          setFunds(result.data.data);
        }
      } catch (e) {
        console.warn('Funds fetch failed:', e);
      }
    };
    fetchFunds();
  }, [connectionStatus]);

  // Fetch gainers/losers
  useEffect(() => {
    if (connectionStatus !== 'connected') return;
    const fetchMovers = async () => {
      try {
        const [gResult, lResult] = await Promise.allSettled([
          angelOneApi.getGainersLosers({ datatype: 'PercOHLCGainer', expirytype: '' }),
          angelOneApi.getGainersLosers({ datatype: 'PercOHLCLoser', expirytype: '' }),
        ]);
        if (gResult.status === 'fulfilled') {
          const gData = gResult.value.data?.data;
          if (Array.isArray(gData)) setGainers(gData.slice(0, 5));
        }
        if (lResult.status === 'fulfilled') {
          const lData = lResult.value.data?.data;
          if (Array.isArray(lData)) setLosers(lData.slice(0, 5));
        }
      } catch (e) { /* silent */ }
    };
    fetchMovers();
    const interval = setInterval(fetchMovers, 60000);
    return () => clearInterval(interval);
  }, [connectionStatus]);

  if (connectionStatus !== 'connected') {
    return (
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-12 text-center">
        <div className="w-16 h-16 mx-auto mb-4 bg-blue-50 rounded-2xl flex items-center justify-center">
          <FaChartLine className="text-2xl text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Connect to Angel One</h3>
        <p className="text-sm text-gray-500 max-w-md mx-auto">
          Login with your Angel One credentials to access live market data, place orders, and manage your portfolio.
        </p>
      </div>
    );
  }

  const availableMargin = funds?.availablecash || funds?.net || profile?.availablecash;

  return (
    <div className="space-y-4">
      {/* Refresh button */}
      <div className="flex justify-end">
        <button
          onClick={() => { refreshPortfolio(); setLoading(true); setTimeout(() => setLoading(false), 2000); }}
          className="text-xs text-blue-600 hover:text-blue-700 flex items-center gap-1"
        >
          <FaSync className={`text-[10px] ${loading ? 'animate-spin' : ''}`} /> Refresh Data
        </button>
      </div>

      {/* Portfolio Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <StatCard
          icon={<FaWallet />}
          label="Available Margin"
          value={availableMargin ? `₹${Number(availableMargin).toLocaleString('en-IN')}` : '—'}
          color="blue"
        />
        <StatCard
          icon={<FaBriefcase />}
          label="Day P&L"
          value={<PnLBadge value={positionSummary.totalPnL} />}
          color={positionSummary.totalPnL >= 0 ? 'green' : 'red'}
        />
        <StatCard
          icon={<FaChartLine />}
          label="Holdings Value"
          value={`₹${holdingSummary.totalCurrent.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`}
          color="blue"
        />
        <StatCard
          icon={<FaArrowUp />}
          label="Holdings P&L"
          value={<PnLBadge value={holdingSummary.totalPnL} />}
          color={holdingSummary.totalPnL >= 0 ? 'green' : 'red'}
        />
      </div>

      {/* Indices */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Market Indices</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {indices.map(idx => {
            const ltp = idx.data?.ltp;
            const close = idx.data?.close;
            const change = ltp && close ? ltp - close : (idx.data?.change || 0);
            const changePct = idx.data?.changePct || (close ? (change / close) * 100 : 0);
            const isUp = change >= 0;

            return (
              <div key={idx.name} className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                <p className="text-[10px] text-gray-400 font-medium">{idx.name}</p>
                <p className="text-lg font-bold text-gray-900 font-mono">
                  {ltp ? ltp.toLocaleString('en-IN', { maximumFractionDigits: 2 }) : '...'}
                </p>
                {ltp ? (
                  <div className={`flex items-center gap-1 text-[10px] font-medium ${isUp ? 'text-green-600' : 'text-red-500'}`}>
                    {isUp ? <FaArrowUp className="text-[8px]" /> : <FaArrowDown className="text-[8px]" />}
                    <span>{Math.abs(change).toFixed(2)}</span>
                    <span>({Math.abs(changePct).toFixed(2)}%)</span>
                  </div>
                ) : (
                  <p className="text-[10px] text-gray-400">Loading...</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Gainers & Losers */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <MoverCard title="Top Gainers" items={gainers} isGainer={true} />
        <MoverCard title="Top Losers" items={losers} isGainer={false} />
      </div>
    </div>
  );
}

function StatCard({ icon, label, value, color }) {
  const colorMap = { blue: 'text-blue-600', green: 'text-green-600', red: 'text-red-600' };
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className={`text-xs ${colorMap[color] || 'text-blue-600'}`}>{icon}</div>
        <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-lg font-bold text-gray-900">{value}</div>
    </div>
  );
}

function MoverCard({ title, items, isGainer }) {
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center gap-2">
        {isGainer ? <FaArrowUp className="text-green-600" /> : <FaArrowDown className="text-red-500" />}
        {title}
      </h3>
      {items.length === 0 ? (
        <p className="text-xs text-gray-400 py-4 text-center">Loading market movers...</p>
      ) : (
        <div className="space-y-2">
          {items.map((item, i) => (
            <div key={i} className="flex items-center justify-between py-1.5 border-b border-gray-100 last:border-0">
              <div>
                <p className="text-xs font-medium text-gray-800">{item.tradingSymbol || item.symbol}</p>
                <p className="text-[10px] text-gray-400">{item.exchange}</p>
              </div>
              <div className="text-right">
                <p className="text-xs font-mono font-semibold text-gray-800">₹{parseFloat(item.ltp || 0).toLocaleString('en-IN')}</p>
                <p className={`text-[10px] font-mono ${isGainer ? 'text-green-600' : 'text-red-500'}`}>
                  {isGainer ? '+' : ''}{parseFloat(item.percentChange || 0).toFixed(2)}%
                </p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
